import { paths } from 'app/paths'
import { AnyPlaybookType } from 'app/types'
import { isQG } from 'modules'
import { useCallback, useMemo } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useBoolean } from './use-boolean'
import { useShouldMatchBrowserLanguage } from './use-should-match-browser-language'

const getBrowserLanguages = () => {
    if (!navigator) {
        return []
    }

    return navigator.languages || (navigator.language ? [navigator.language] : [])
}

const findBestMatchingLanguageCode = (availableTranslations: string[]) => {
    const browserLanguages = getBrowserLanguages()

    if (!availableTranslations?.length || !browserLanguages?.length) {
        return null
    }

    const exactMatch = availableTranslations.find(lang => browserLanguages.includes(lang))

    if (exactMatch) {
        return exactMatch
    }

    const baseMatch = availableTranslations.find(lang => {
        return browserLanguages.find(browserLang => {
            const baseLang = browserLang.split('-')[0].toLowerCase()

            return lang.toLowerCase().includes(baseLang)
        })
    })

    return baseMatch || null
}

type Props = {
    playbookPathTemplate?: string
}

export const useBrowserLanguagePreferences = ({ playbookPathTemplate }: Props = {}) => {
    const wasInUse = useBoolean()
    const shouldMatchBrowserLanguage = useShouldMatchBrowserLanguage()
    const history = useHistory()
    const historyPush = history.push

    const openPlaybookPage = useCallback(
        (playbookId: string) => {
            const path = generatePath(playbookPathTemplate ?? paths.playbookDetails, { playbookId })
            const fullPath = location.search ? `${path}${location.search}` : path
            historyPush(fullPath)
        },
        [historyPush, playbookPathTemplate],
    )
    const setWasInUse = wasInUse.setTrue
    const openPreferredLanguage = useCallback(
        (playbook: AnyPlaybookType) => {
            if (wasInUse.isTrue || !isQG(playbook) || !shouldMatchBrowserLanguage) {
                return
            }

            const translations = playbook?.translations

            if (!translations?.length) {
                return
            }

            const langCodes = translations.map(t => t.language.langCode)
            const preferredCode = findBestMatchingLanguageCode(langCodes)

            const playbookWithPreferredLanguage = translations.find(t => t.language.langCode === preferredCode)

            if (!playbookWithPreferredLanguage) {
                return
            }

            setWasInUse()
            openPlaybookPage(playbookWithPreferredLanguage.id)
        },
        [openPlaybookPage, setWasInUse, wasInUse.isTrue, shouldMatchBrowserLanguage],
    )

    return useMemo(
        () => ({
            openPreferredLanguage,
        }),
        [openPreferredLanguage],
    )
}
